import { Link } from 'react-router-dom';
import { useState } from 'react';
import { DBProduct } from '@/hooks/useProducts';
import { Star, Package } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { useTranslations } from '@/hooks/useTranslations';
import { transformImage } from '@/lib/imageTransform';
import { DisplayPrice } from '@/components/DisplayPrice';

interface ProductCardProps {
  product: DBProduct;
}

export default function ProductCard({ product }: ProductCardProps) {
  const { localePath } = useLanguage();
  const { t } = useTranslations();
  const [imgFailed, setImgFailed] = useState(false);
  const cheapest = product.product_variations.length > 0
    ? product.product_variations.reduce((min, v) => (v.price < min.price ? v : min), product.product_variations[0])
    : null;
  const minPrice = cheapest?.price ?? 0;
  const minVariationId = (cheapest as any)?.id as string | undefined;

  return (
    <Link
      to={localePath(`/produkt/${product.slug}`)}
      className="group flex flex-col items-center rounded-xl border bg-card p-5 text-center transition-all hover:border-primary/30 hover:shadow-md"
    >
      <div className="mb-3 flex h-16 w-16 items-center justify-center">
        {product.image_url && !imgFailed ? (
          <img
            src={transformImage(product.image_url, { width: 56, height: 56, resize: 'contain' })}
            alt={`${product.title} kaufen – BigFollow`}
            className="h-14 w-14 object-contain transition-transform group-hover:scale-110"
            loading="lazy"
            width={56}
            height={56}
            onError={() => setImgFailed(true)}
          />
        ) : (
          <Package className="h-10 w-10 text-muted-foreground/40" aria-hidden />
        )}
      </div>
      <h3 className="mb-1 text-sm font-semibold leading-tight">{product.title}</h3>
      <div className="mb-2 flex items-center gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            className={`h-3 w-3 ${i < Math.round(product.rating ?? 0) ? 'fill-warning text-warning' : 'text-border'}`}
          />
        ))}
        <span className="ml-1 text-[10px] text-muted-foreground">({product.review_count ?? 0})</span>
      </div>
      <span className="rounded-md bg-accent px-2.5 py-1 font-mono text-xs font-semibold text-accent-foreground">
        {t('product.from_price')} <DisplayPrice chf={minPrice} variationId={minVariationId} />
      </span>
    </Link>
  );
}
